"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.validateUserPermissions = exports.userHasPermission = void 0;
const _1 = require(".");
const types_1 = require("./types");
/**
 * Checks if the User has the specified permission to access a resource, with target users if requesting other user's resources.
 * @param resolver Resolver to resolve data for the permission check, such as getting permissions, or validating access.
 * @param userId The User to validate.
 * @param request The Permission to validate the user has.
 * @param targetUserIds Target users to test the permission against, if applicable, otherwise pass an empty array.
 * @returns Has the requested permission?
 */
async function userHasPermission(resolver, userId, request, targetUserIds) {
    const requestedResource = request.resource || types_1.PermissionResourceAlias.All;
    const isRequestingUserResource = (resource) => {
        return ((typeof resource !== 'string' &&
            resource.qualifier === types_1.PermissionResourceQualifier.User &&
            resource.identifier === userId) ||
            resource === types_1.PermissionResourceAlias.Own);
    };
    // Get all permissions for the database that match the action and resource.
    const permissionsForResource = (await resolver.getPermissions(userId, [request.action, types_1.PermissionAction.All], [request.resourceType, types_1.PermissionResourceType.All]))
        .map(_1.ParsePermission)
        .sort((a, b) => {
        const isAString = typeof a.resource === 'string';
        const isBString = typeof b.resource === 'string';
        // Prioritise string aliases above PermissionResource, as it might require a lookup.
        if (isAString && !isBString) {
            return -1;
        }
        if (!isAString && isBString) {
            return 1;
        }
        // Prioritise All Alias above other Aliases.
        const isAAll = a.resource === types_1.PermissionResourceAlias.All;
        const isBAll = b.resource === types_1.PermissionResourceAlias.All;
        if (isAAll && !isBAll) {
            return -1;
        }
        if (!isBAll && isAAll) {
            return 1;
        }
        return 0;
    });
    // Perform the Permission Resource matching logic.
    // This can be executed multiple times if an array of identifiers are specified.
    // If that is the case, all must match.
    const matchesResourceAsync = async (requestingPermissionResource) => {
        const isUserResource = typeof requestingPermissionResource !== 'string' &&
            requestingPermissionResource.qualifier === types_1.PermissionResourceQualifier.User;
        // If the requestingPermissionResource is a resource entity of type user, use that instead as the resource target.
        const requestingResourceUserTargets = isUserResource
            ? [requestingPermissionResource.identifier]
            : targetUserIds;
        // Is the owner of the resource? Set to an Alias.
        if (isRequestingUserResource(requestingPermissionResource)) {
            requestingPermissionResource = types_1.PermissionResourceAlias.Own;
        }
        for (const permission of permissionsForResource) {
            // Can access all of this resource?
            if (permission.resource === types_1.PermissionResourceAlias.All) {
                return true;
            }
            if (permission.resource === types_1.PermissionResourceAlias.Own || isRequestingUserResource(permission.resource)) {
                if (requestingPermissionResource === types_1.PermissionResourceAlias.Own) {
                    return true;
                }
                continue;
            }
            // Only resource entities from here on.
            const resourceEntity = permission.resource;
            // Is this a direct permissive match?
            if (_1.MatchesPermissionResourceRequest(permission, requestedResource)) {
                return true;
            }
            // Check if qualifier matches against resource targets.
            if (requestingResourceUserTargets && requestingResourceUserTargets.length > 0) {
                const { qualifier, identifier } = resourceEntity;
                switch (qualifier) {
                    // Check if target users matches the role qualifier.
                    case types_1.PermissionResourceQualifier.Role:
                        if (await resolver.isAccessibleByRole(requestingResourceUserTargets, [identifier])) {
                            return true;
                        }
                        break;
                    // Check if target user ids matches permission exactly.
                    // This won't work if we have more than one userid being checked.
                    case types_1.PermissionResourceQualifier.User:
                        if (requestingResourceUserTargets.every((targetUserId) => identifier === targetUserId)) {
                            return true;
                        }
                        break;
                }
            }
        }
        // Couldn't find a matching permission.
        return false;
    };
    // If request is an array of ids, go through all of them and match individually.
    if (typeof requestedResource !== 'string') {
        const { qualifier, identifier } = requestedResource;
        if (Array.isArray(identifier)) {
            return (await Promise.all(identifier.map((id) => matchesResourceAsync({ qualifier, identifier: id })))).every((match) => match === true);
        }
        return matchesResourceAsync({ qualifier, identifier });
    }
    return matchesResourceAsync(requestedResource);
}
exports.userHasPermission = userHasPermission;
/**
 * Validates the User has the specified permissions to access a resource, with target users if requesting other user's resources.
 * @param resolver Resolver to resolve data for the permission check, such as getting permissions, or validating access.
 * @param userId The User to validate.
 * @param permissions The Permissions to validate the user has, is a list, optionally with conditions.
 * @param targetUserIds Target users to test permissions against, if applicable, otherwise pass an empty array.
 * @returns A list of permissions that failed validation.
 */
async function validateUserPermissions(resolver, userId, permissions, targetUserIds) {
    const failedPermissions = [];
    if (!Array.isArray(permissions)) {
        permissions = [permissions];
    }
    // Just in case this function isn't called properly.
    targetUserIds = targetUserIds || [];
    for (const permission of permissions) {
        if ('condition' in permission) {
            switch (permission.condition) {
                case 'OR':
                    let metAnOrCondition = false;
                    const failedOrPermissions = [];
                    for (const subPermission of permission.permissions) {
                        if (!(await userHasPermission(resolver, userId, subPermission, targetUserIds))) {
                            failedOrPermissions.push(subPermission);
                        }
                        else {
                            metAnOrCondition = true;
                        }
                    }
                    // Only push as failed if none of the checks succeed.
                    if (!metAnOrCondition) {
                        failedPermissions.push(...failedOrPermissions);
                    }
                    break;
                case 'AND':
                    for (const subPermission of permission.permissions) {
                        if (!(await userHasPermission(resolver, userId, subPermission, targetUserIds))) {
                            failedPermissions.push(subPermission);
                        }
                    }
                    break;
                default:
                    throw new Error(`Unknown Permission Condition: ${permission.condition}`);
            }
        }
        else if (!(await userHasPermission(resolver, userId, permission, targetUserIds))) {
            failedPermissions.push(permission);
        }
    }
    return failedPermissions;
}
exports.validateUserPermissions = validateUserPermissions;
//# sourceMappingURL=resolve.js.map